import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { colors, styles } from '../styles/styles';
import SearchInput from '../components/common/SearchInput';
import { 
  addThoughtToCollectionsBatch, 
  removeThoughtFromCollectionsBatch, 
  getCollections 
} from '../services/collectionService';
import PageHeader from '../components/PageHeader';
import SelectedCounter from '../components/common/SelectedCounter';
import StaggeredAnimation from '../components/common/StaggeredAnimation';
import useSelectionState from '../hooks/useSelectionState';
import { handleRelationshipUpdate } from '../utils/collectionUtils';
import CreateCollectionModal from '../components/CreateCollectionModal';
import PlusIcon from '../components/common/PlusIcon';

/**
 * Page for adding a single thought to multiple collections
 */
const AddToCollectionsPage = ({ navigateWithFade }) => {
    const { thoughtId } = useParams();
    const location = useLocation();
    const thoughtText = location.state?.thoughtText || "Thought";
    
    const [collections, setCollections] = useState([]);
    const [existingCollectionIds, setExistingCollectionIds] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    
    // Use selection state hook for collection selection
    const { 
        selectedItems: selectedCollectionIds, 
        setSelectedItems: setSelectedCollectionIds,
        toggleItem: toggleCollectionSelection,
        isSelected: isCollectionSelected,
        count: selectedCount
    } = useSelectionState([]);
    
    // Function to fetch collections (used after creation or on initial load)
    const fetchCollections = async () => {
        setLoading(true);
        try {
            // Get all collections
            const allCollections = await getCollections();
            
            // Filter out tag collections and default collection
            const filteredCollections = allCollections.filter(collection => 
                !collection.isTagCollection && !collection.isDefault
            );
            setCollections(filteredCollections);
            
            // Make sure we keep the current selection state
            if (existingCollectionIds.length > 0) {
                const filteredIds = existingCollectionIds.filter(id => {
                    const collection = allCollections.find(c => c.id === id);
                    return collection && !collection.isTagCollection && !collection.isDefault;
                });
                setExistingCollectionIds(filteredIds);
            }
        } catch (error) {
            console.error('Error fetching collections:', error);
        } finally {
            setLoading(false);
        }
    };
    
    // Handle collection creation success
    const handleCollectionCreated = () => {
        setShowCreateModal(false);
        fetchCollections(); // Refresh the collections list
    };
    
    // Fetch all collections and existing collections for this thought
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                // Get all collections
                const allCollections = await getCollections();
                
                // Filter out tag collections and default collection
                const filteredCollections = allCollections.filter(collection => 
                    !collection.isTagCollection && !collection.isDefault
                );
                setCollections(filteredCollections);
                
                // Get existing collection ids from state
                if (location.state?.existingCollectionIds) {
                    const ids = location.state.existingCollectionIds;
                    // Filter out tag collection IDs and default collection
                    const filteredIds = ids.filter(id => {
                        const collection = allCollections.find(c => c.id === id);
                        return collection && !collection.isTagCollection && !collection.isDefault;
                    });
                    setExistingCollectionIds(filteredIds);
                    setSelectedCollectionIds(filteredIds);
                }
            } catch (error) {
                console.error('Error fetching collections:', error);
            } finally {
                setLoading(false);
            }
        };
        
        fetchData();
    }, [location.state, thoughtId, setSelectedCollectionIds]);
    
    // Filter collections based on search term
    const filteredCollections = collections.filter(collection => {
        if (!searchTerm) return true;
        
        const searchLower = searchTerm.toLowerCase();
        return collection.name.toLowerCase().includes(searchLower);
    });
    
    // Handle navigating back
    const handleBack = () => {
        navigateWithFade(`/thought/${thoughtId}`);
    };
    
    // Handle saving collection selections
    const handleSaveCollections = async () => {
        if (isSubmitting) return;
        
        await handleRelationshipUpdate({
            selectedIds: selectedCollectionIds,
            existingIds: existingCollectionIds,
            addItemsFunction: addThoughtToCollectionsBatch,
            removeItemsFunction: removeThoughtFromCollectionsBatch,
            targetId: thoughtId,
            setIsSubmitting,
            setSuccessMessage,
            navigateWithFade,
            navigateToPath: `/thought/${thoughtId}`,
            itemType: 'collection'
        });
    };
    
    return (
        <div style={{ width: '100%' }} className="smooth-scroll">
            {/* Remove fade animation styles since AppContent handles transitions */}
            <StaggeredAnimation containerClass="collections-feed"/>
            
            {/* Header with back button and title */}
            <PageHeader 
                onBack={handleBack}
                title={{ text: "Add to Collections" }}
                rightContent={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div 
                            onClick={() => setShowCreateModal(true)}
                            style={{ 
                                cursor: 'pointer', 
                                display: 'flex', 
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '4px'
                            }}
                        >
                            <PlusIcon width={20} height={20} color={colors.textSecondary} />
                        </div>
                        <SelectedCounter count={selectedCount} />
                    </div>
                }
            />
            
            {/* Thought preview */}
            <div style={{
                margin: '0 16px 16px',
                padding: '12px 16px',
                backgroundColor: colors.surfaceElevated,
                borderRadius: '12px',
                maxHeight: '80px',
                overflow: 'hidden',
                position: 'relative',
            }}>
                <p style={{
                    margin: 0,
                    fontSize: '14px',
                    lineHeight: '1.4',
                    color: colors.textPrimary,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                }}>
                    {thoughtText}
                </p>
                <div style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '20px',
                    background: `linear-gradient(to bottom, transparent, ${colors.surfaceElevated})`,
                }}></div>
            </div>
            
            {/* Search row */}
            <div style={styles.pageHeaderContainer}>
                <div style={styles.desktopUtilityBarContainer}>
                    <SearchInput
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search collections..."
                        type="desktop"
                    />
                </div>
            </div>
            
            {/* Content */}
            <div style={{ width: '100%' }}>
                {successMessage ? (
                    <div style={{ 
                        textAlign: 'center', 
                        padding: '20px', 
                        color: successMessage.includes('Failed') ? colors.error : colors.accent.green,
                        fontWeight: '500'
                    }}>
                        {successMessage}
                    </div>
                ) : loading ? (
                    <div style={{ 
                        textAlign: 'center', 
                        padding: '40px', 
                        color: colors.textSecondary 
                    }}>
                        Loading collections...
                    </div>
                ) : filteredCollections.length === 0 ? (
                    <div style={{ 
                        textAlign: 'center', 
                        padding: '40px 0', 
                        color: colors.textSecondary,
                        marginTop: '16px'
                    }}>
                        <p>No collections found</p>
                    </div>
                ) : (
                    <div className="collections-feed" style={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        marginBottom: '80px' // Space for the footer
                    }}>
                        {filteredCollections.map((collection, index) => (
                            <div 
                                key={collection.id}
                                onClick={() => toggleCollectionSelection(collection.id)}
                                className="list-item"
                                style={{ 
                                    padding: '16px',
                                    cursor: 'pointer',
                                    width: '100%',
                                    backgroundColor: colors.background,
                                    borderBottom: index === filteredCollections.length - 1 ? 'none' : `1px solid ${colors.divider}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                <span style={{
                                    fontSize: '15px',
                                    lineHeight: '1.5',
                                    color: colors.textPrimary,
                                    flex: 1,
                                }}>
                                    {collection.name}
                                </span>
                                
                                {/* Checkmark indicator */}
                                <div style={{
                                    width: '20px',
                                    height: '20px',
                                    borderRadius: '10px',
                                    backgroundColor: isCollectionSelected(collection.id) ? colors.accent.white : 'transparent',
                                    border: isCollectionSelected(collection.id) ? 'none' : `1px solid ${colors.divider}`,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}>
                                    {isCollectionSelected(collection.id) && (
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M20 6L9 17L4 12" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                
                {/* Footer with action buttons */}
                <div style={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    padding: '16px',
                    backgroundColor: colors.surfaceElevated,
                    borderTop: `1px solid ${colors.divider}`,
                    display: 'flex',
                    justifyContent: 'flex-end',
                    zIndex: 10
                }}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                            onClick={handleBack}
                            style={{
                                backgroundColor: 'transparent',
                                color: colors.accent.white,
                                border: 'none',
                                borderRadius: '20px',
                                padding: '8px 16px',
                                fontWeight: 'bold',
                                fontSize: '12px',
                                cursor: 'pointer'
                            }}
                        >
                            Cancel
                        </button>
                        
                        <button
                            onClick={handleSaveCollections}
                            disabled={isSubmitting}
                            style={{
                                backgroundColor: colors.accent.white,
                                color: 'black',
                                border: 'none',
                                borderRadius: '20px',
                                padding: '8px 16px',
                                fontWeight: 'bold',
                                fontSize: '12px',
                                cursor: !isSubmitting ? 'pointer' : 'not-allowed',
                                opacity: !isSubmitting ? 1 : 0.5
                            }}
                        >
                            {isSubmitting ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </div>
            </div>
            
            {/* Create Collection Modal */}
            <CreateCollectionModal 
                isVisible={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onSuccess={handleCollectionCreated}
            />
        </div>
    );
};

export default AddToCollectionsPage; 